import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCurrency } from '@/lib/currency-context';
import { admin } from '@/lib/api';
import { Eye, Search, CheckCircle2, Minus, Package, Plane, Building2, Car, CarTaxiFront, Loader2, RefreshCw } from 'lucide-react';
import SmartPagination from '@/components/SmartPagination';
import OrderDetailModal from './OrderDetailModal';
import { toast } from 'sonner';
import { formatPaymentMethodLabel, resolveOrderPaymentMethod } from '@/lib/payment-methods';

const statusBadge = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-indigo-100 text-indigo-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const paymentBadge = {
  pending: 'bg-yellow-50 text-yellow-700',
  paid: 'bg-green-50 text-green-700',
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  refunded: 'bg-gray-50 text-gray-700',
  initiated: 'bg-yellow-50 text-yellow-700',
};

const paymentStatusLabel = {
  pending: 'BEKLEMEDE',
  paid: 'ÖDENDİ',
  completed: 'ÖDENDİ',
  failed: 'BAŞARISIZ',
  refunded: 'İADE',
  initiated: 'BAŞLATILDI',
};

const limoTypeLabel = (type) =>
  ({ transfer: 'Transfer', hourly: 'Saatlik', delivery: 'Teslimat' }[type] || type || '-');

const limoStatusLabel = (status) =>
  ({
    pending: 'Beklemede',
    offers_received: 'Teklif geldi',
    booked: 'Rezerve',
    cancelled: 'İptal',
    expired: 'Süresi doldu',
  }[status] || status || '-');

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 16);
  return d.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Ürün / otel / uçak / araç kiralama */
const TAB_PRODUCTS = 'products';
const TAB_HOTELS = 'hotels';
const TAB_FLIGHTS = 'flights';
const TAB_CARS = 'cars';
const TAB_LIMO = 'limo';

const OrdersTab = ({ orders, handleOrderStatusChange, hasPermission }) => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const canViewTravel = !!hasPermission?.('orders.view');

  const [orderTab, setOrderTab] = useState(TAB_PRODUCTS);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const [hotelBookings, setHotelBookings] = useState([]);
  const [flightBookings, setFlightBookings] = useState([]);
  const [carBookings, setCarBookings] = useState([]);
  const [limoBookings, setLimoBookings] = useState([]);
  const [hotelTotal, setHotelTotal] = useState(0);
  const [flightTotal, setFlightTotal] = useState(0);
  const [carTotal, setCarTotal] = useState(0);
  const [limoTotal, setLimoTotal] = useState(0);
  const [travelLoading, setTravelLoading] = useState(false);

  const [travelSearch, setTravelSearch] = useState('');
  const [travelDetail, setTravelDetail] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const fetchTravelLists = useCallback(async () => {
    if (!canViewTravel) return;
    setTravelLoading(true);
    try {
      const [hRes, fRes, cRes, lRes] = await Promise.allSettled([
        admin.hotelBookings({ limit: 2000 }),
        admin.flightBookings({ limit: 2000 }),
        admin.carBrokerBookings({ limit: 2000 }),
        admin.taxiBookingRequests({ limit: 2000 }),
      ]);
      const take = (settled) => (settled.status === 'fulfilled' ? settled.value?.data : null);
      const hotels = take(hRes);
      const flights = take(fRes);
      const cars = take(cRes);
      const limos = take(lRes);
      if (hotels) {
        setHotelBookings(hotels.bookings || []);
        setHotelTotal(hotels.total ?? (hotels.bookings?.length || 0));
      }
      if (flights) {
        setFlightBookings(flights.bookings || []);
        setFlightTotal(flights.total ?? (flights.bookings?.length || 0));
      }
      if (cars) {
        setCarBookings(cars.bookings || []);
        setCarTotal(cars.total ?? (cars.bookings?.length || 0));
      }
      if (limos) {
        setLimoBookings(limos.bookings || []);
        setLimoTotal(limos.total ?? (limos.bookings?.length || 0));
      }
      const failed = [hRes, fRes, cRes, lRes].filter((r) => r.status === 'rejected');
      if (failed.length) {
        const err = failed[0].reason;
        toast.error(
          err?.response?.data?.detail ||
            t('admin.travelOrdersLoadError', 'Otel/uçak/araç listesi alınamadı (yetki veya API).'),
        );
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e.response?.data?.detail ||
          t('admin.travelOrdersLoadError', 'Otel/uçak/araç listesi alınamadı (yetki veya API).'),
      );
    } finally {
      setTravelLoading(false);
    }
  }, [canViewTravel, t]);

  useEffect(() => {
    if (canViewTravel) fetchTravelLists();
  }, [canViewTravel, fetchTravelLists]);

  const filteredMarketplace = useMemo(() => {
    return (orders || []).filter((o) => {
      const matchSearch =
        !search ||
        (o.id || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.order_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.guest_email || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.user_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.shipping_address?.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.seller_name || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || o.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [orders, search, statusFilter]);

  const filteredHotels = useMemo(() => {
    const q = travelSearch.trim().toLowerCase();
    return hotelBookings.filter((b) => {
      if (!q) return true;
      const g = b.guest || {};
      return (
        String(b.id || '').toLowerCase().includes(q) ||
        String(b.hotel_name || '').toLowerCase().includes(q) ||
        String(g.email || '').toLowerCase().includes(q) ||
        `${g.first_name || ''} ${g.last_name || ''}`.toLowerCase().includes(q)
      );
    });
  }, [hotelBookings, travelSearch]);

  const filteredFlights = useMemo(() => {
    const q = travelSearch.trim().toLowerCase();
    return flightBookings.filter((b) => {
      if (!q) return true;
      const c = b.contact || {};
      return (
        String(b.id || '').toLowerCase().includes(q) ||
        String(b.pnr || '').toLowerCase().includes(q) ||
        String(b.status || '').toLowerCase().includes(q) ||
        String(c.email || '').toLowerCase().includes(q)
      );
    });
  }, [flightBookings, travelSearch]);

  const filteredCars = useMemo(() => {
    const q = travelSearch.trim().toLowerCase();
    return carBookings.filter((b) => {
      if (!q) return true;
      const c = b.customer || {};
      const v = b.vehicle || {};
      const br = b.broker || {};
      const s = b.search || {};
      return (
        String(b.id || '').toLowerCase().includes(q) ||
        String(v.vehicleName || '').toLowerCase().includes(q) ||
        String(c.email || '').toLowerCase().includes(q) ||
        `${c.name || ''} ${c.surname || ''}`.toLowerCase().includes(q) ||
        String(br.reservation_number || '').toLowerCase().includes(q) ||
        String(s.pickupLocationName || '').toLowerCase().includes(q) ||
        String(b.booking_status || '').toLowerCase().includes(q)
      );
    });
  }, [carBookings, travelSearch]);

  const filteredLimo = useMemo(() => {
    const q = travelSearch.trim().toLowerCase();
    return limoBookings.filter((b) => {
      if (!q) return true;
      return (
        String(b.request_number || '').toLowerCase().includes(q) ||
        String(b.contact_name || '').toLowerCase().includes(q) ||
        String(b.contact_email || '').toLowerCase().includes(q) ||
        String(b.contact_phone || '').toLowerCase().includes(q) ||
        String(b.pickup_address || '').toLowerCase().includes(q) ||
        String(b.dropoff_address || '').toLowerCase().includes(q) ||
        String(b.status || '').toLowerCase().includes(q) ||
        String(b.preferred_vehicle_class || '').toLowerCase().includes(q)
      );
    });
  }, [limoBookings, travelSearch]);

  const activePagedList =
    orderTab === TAB_PRODUCTS
      ? filteredMarketplace
      : orderTab === TAB_HOTELS
        ? filteredHotels
        : orderTab === TAB_FLIGHTS
          ? filteredFlights
          : orderTab === TAB_LIMO
            ? filteredLimo
            : filteredCars;
  const totalPages = Math.ceil((activePagedList?.length || 0) / pageSize);
  const paged = (activePagedList || []).slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [orderTab, search, statusFilter, travelSearch]);

  const travelDetailJson = travelDetail?.data
    ? JSON.stringify(travelDetail.data, null, 2)
    : '';

  const travelDetailTitle =
    travelDetail?.type === 'hotel'
      ? t('admin.bookingHotelDetail', 'Otel rezervasyonu — detay')
      : travelDetail?.type === 'flight'
        ? t('admin.bookingFlightDetail', 'Uçuş rezervasyonu — detay')
        : travelDetail?.type === 'limo'
          ? t('admin.bookingLimoDetail', 'Taksi & Limuzin talebi — detay')
          : t('admin.bookingCarDetail', 'Araç kiralama — detay');

  return (
    <>
      <Tabs
        value={orderTab}
        onValueChange={(v) => {
          setOrderTab(v);
          setTravelSearch('');
        }}
        className="space-y-4"
      >
        <TabsList className="grid w-full max-w-5xl grid-cols-2 sm:grid-cols-5 h-auto p-1">
          <TabsTrigger value={TAB_PRODUCTS} className="gap-1.5 text-xs sm:text-sm">
            <Package className="w-3.5 h-3.5 shrink-0" />
            {t('admin.ordersTab.products', 'Ürün')} ({orders?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value={TAB_HOTELS} className="gap-1.5 text-xs sm:text-sm" disabled={!canViewTravel}>
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            {t('admin.ordersTab.hotels', 'Otel')} ({hotelTotal})
          </TabsTrigger>
          <TabsTrigger value={TAB_FLIGHTS} className="gap-1.5 text-xs sm:text-sm" disabled={!canViewTravel}>
            <Plane className="w-3.5 h-3.5 shrink-0" />
            {t('admin.ordersTab.flights', 'Uçak')} ({flightTotal})
          </TabsTrigger>
          <TabsTrigger value={TAB_CARS} className="gap-1.5 text-xs sm:text-sm" disabled={!canViewTravel}>
            <Car className="w-3.5 h-3.5 shrink-0" />
            {t('admin.ordersTab.cars', 'Araç')} ({carTotal})
          </TabsTrigger>
          <TabsTrigger value={TAB_LIMO} className="gap-1.5 text-xs sm:text-sm" disabled={!canViewTravel}>
            <CarTaxiFront className="w-3.5 h-3.5 shrink-0" />
            {t('admin.ordersTab.limo', 'Limuzin')} ({limoTotal})
          </TabsTrigger>
        </TabsList>

        {canViewTravel && orderTab !== TAB_PRODUCTS && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Button type="button" variant="outline" size="sm" onClick={() => fetchTravelLists()} disabled={travelLoading}>
              {travelLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {t('common.refresh', 'Yenile')}
            </Button>
            <span>
              {t(
                'admin.ordersTab.travelNote',
                'Liste hotel_bookings, flight_bookings, car_broker_bookings ve taxi_booking_requests koleksiyonlarındandır.'
              )}
            </span>
          </div>
        )}

        <TabsContent value={TAB_PRODUCTS} className="mt-0">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.ordersMarketplaceTitle', 'Marketplace Siparişleri')} ({filteredMarketplace.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input
                      placeholder="Siparis No, email, ad..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                      className="pl-8 h-8 w-52 text-sm"
                      data-testid="order-search-input"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-8 text-sm border rounded-md px-2 bg-white"
                    data-testid="order-status-filter"
                  >
                    <option value="all">Tum Durumlar</option>
                    <option value="pending">Beklemede</option>
                    <option value="confirmed">Onaylandi</option>
                    <option value="processing">Hazirlaniyor</option>
                    <option value="shipped">Kargoda</option>
                    <option value="delivered">Teslim Edildi</option>
                    <option value="cancelled">İptal edildi</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-xs">Siparis No</TableHead>
                    <TableHead className="font-semibold text-xs">Musteri</TableHead>
                    <TableHead className="font-semibold text-xs">Tedarikci</TableHead>
                    <TableHead className="font-semibold text-xs">Toplam</TableHead>
                    <TableHead className="font-semibold text-xs">Tarih</TableHead>
                    <TableHead className="font-semibold text-xs">Durum</TableHead>
                    <TableHead className="font-semibold text-xs">Odeme</TableHead>
                    <TableHead className="font-semibold text-xs" title="Paraşüt">
                      Paraşüt
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-right">Islem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-400">
                        {search ? 'Arama sonucu bulunamadi' : 'Henuz siparis yok'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paged.map((order) => (
                      <TableRow
                        key={order.id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setSelectedOrderId(order.id)}
                        data-testid={`order-row-${order.id}`}
                      >
                        <TableCell>
                          <div>
                            <span className="font-mono text-xs font-medium text-gray-900">
                              {order.order_number || order.id?.slice(0, 8)}
                            </span>
                            {order.is_guest_order && (
                              <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0">
                                Misafir
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>
                            <p className="font-medium text-gray-900 truncate max-w-[140px]">
                              {order.shipping_address?.full_name || order.guest_email || order.user_id?.slice(0, 10)}
                            </p>
                            {(order.guest_email || order.user_email) && (
                              <p className="text-xs text-gray-400 truncate max-w-[140px]">
                                {order.guest_email || order.user_email}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700 max-w-[140px]">
                          <span className="truncate block" title={order.seller_name || ''}>
                            {order.seller_name || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="font-semibold text-sm">{formatPrice(order.total || 0)}</TableCell>
                        <TableCell className="text-xs text-gray-600">{formatDate(order.created_at)}</TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadge[order.status] || 'bg-gray-100 text-gray-800'}`}
                            title={order.cancelled_by_name || order.cancelled_by_email || ''}
                          >
                            {order.status === 'cancelled' ? 'İPTAL EDİLDİ' : order.status?.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] text-gray-600">
                              {formatPaymentMethodLabel(resolveOrderPaymentMethod(order))}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-medium w-fit ${paymentBadge[order.payment_status] || 'bg-gray-100'}`}
                            >
                              {paymentStatusLabel[order.payment_status] || (order.payment_status || 'N/A').toUpperCase()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {order.parasut_sales_invoice_id ? (
                            <span
                              className="inline-flex items-center gap-1 text-emerald-700"
                              title={`Paraşüt fatura ID: ${order.parasut_sales_invoice_id}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                              <span className="text-[10px] font-medium">OK</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-gray-400" title="Paraşüt kaydı yok">
                              <Minus className="w-3.5 h-3.5 shrink-0" />
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrderId(order.id);
                            }}
                            data-testid={`order-detail-btn-${order.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> Detay
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {Math.ceil(filteredMarketplace.length / pageSize) > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(filteredMarketplace.length / pageSize)}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={TAB_HOTELS} className="mt-0">
          <Card>
            <CardHeader className="pb-3 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.hotelBookingList', 'Otel rezervasyonları')} ({filteredHotels.length} / DB: {hotelTotal})
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="ID, otel adı, misafir e-postası..."
                    value={travelSearch}
                    onChange={(e) => setTravelSearch(e.target.value)}
                    className="pl-8 h-8 w-64 text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {travelLoading && hotelBookings.length === 0 ? (
                <div className="flex justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-xs">Kayıt</TableHead>
                      <TableHead className="font-semibold text-xs">Otel</TableHead>
                      <TableHead className="font-semibold text-xs">Misafir</TableHead>
                      <TableHead className="font-semibold text-xs">Giriş / Çıkış</TableHead>
                      <TableHead className="font-semibold text-xs">Rezervasyon</TableHead>
                      <TableHead className="font-semibold text-xs">Ödeme</TableHead>
                      <TableHead className="font-semibold text-xs text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          {hotelTotal === 0
                            ? t('admin.noHotelBookings', 'Kayıtlı otel rezervasyonu yok.')
                            : t('admin.noHotelMatch', 'Arama ile eşleşen otel rezervasyonu yok.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((b) => {
                        const g = b.guest || {};
                        const pay = b.payment || {};
                        return (
                          <TableRow key={b.id} className="hover:bg-muted/40">
                            <TableCell className="font-mono text-xs">{b.id}</TableCell>
                            <TableCell className="max-w-[180px]">
                              <p className="font-medium text-sm truncate">{b.hotel_name || '-'}</p>
                              <p className="text-[10px] text-muted-foreground truncate">pool: {b.pool_search_id || '-'}</p>
                            </TableCell>
                            <TableCell className="text-sm">
                              <span className="block truncate max-w-[160px]">
                                {g.first_name} {g.last_name}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-[160px] block">
                                {g.email}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {b.check_in || '-'} → {b.check_out || '-'}
                              <span className="block text-muted-foreground">{b.nights} gece</span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {(b.booking_status || '').replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentBadge[pay.status] || 'bg-gray-100 text-gray-800'}`}
                              >
                                {(pay.status || '—').toUpperCase()}
                              </span>
                              {b.pricing?.total_price_try != null && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  ≈ TRY {Number(b.pricing.total_price_try).toFixed(0)}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setTravelDetail({ type: 'hotel', data: b })}>
                                <Eye className="w-3.5 h-3.5 mr-1" /> JSON
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
              {orderTab === TAB_HOTELS && totalPages > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} pageSize={pageSize} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={TAB_FLIGHTS} className="mt-0">
          <Card>
            <CardHeader className="pb-3 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.flightBookingList', 'Uçuş rezervasyonları')} ({filteredFlights.length} / DB: {flightTotal})
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="ID, PNR, e-posta, durum..."
                    value={travelSearch}
                    onChange={(e) => setTravelSearch(e.target.value)}
                    className="pl-8 h-8 w-64 text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {travelLoading && flightBookings.length === 0 ? (
                <div className="flex justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-xs">Kayıt</TableHead>
                      <TableHead className="font-semibold text-xs">PNR</TableHead>
                      <TableHead className="font-semibold text-xs">İletişim</TableHead>
                      <TableHead className="font-semibold text-xs">Sağlayıcı</TableHead>
                      <TableHead className="font-semibold text-xs">Durum</TableHead>
                      <TableHead className="font-semibold text-xs">Tutar</TableHead>
                      <TableHead className="font-semibold text-xs">Tarih</TableHead>
                      <TableHead className="font-semibold text-xs text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          {flightTotal === 0
                            ? t('admin.noFlightBookings', 'Kayıtlı uçuş rezervasyonu yok.')
                            : t('admin.noFlightMatch', 'Arama ile eşleşen uçuş rezervasyonu yok.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((b) => {
                        const c = b.contact || {};
                        const total = b.total_with_fee ?? b.base_total;
                        const cur = b.currency || 'EUR';
                        return (
                          <TableRow key={b.id} className="hover:bg-muted/40">
                            <TableCell className="font-mono text-xs max-w-[100px] truncate" title={b.id}>{b.id}</TableCell>
                            <TableCell className="font-mono text-xs font-medium">{b.pnr || b.airline_pnr || '-'}</TableCell>
                            <TableCell className="text-sm">
                              <span className="block truncate max-w-[150px]">
                                {c.name} {c.surname}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-[150px] block">{c.email}</span>
                            </TableCell>
                            <TableCell className="text-xs uppercase">{b.provider || 'gts'}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-[10px] font-normal">
                                {String(b.status || '—')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {typeof total === 'number' ? formatPrice(total, cur) : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(b.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setTravelDetail({ type: 'flight', data: b })}>
                                <Eye className="w-3.5 h-3.5 mr-1" /> JSON
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
              {orderTab === TAB_FLIGHTS && totalPages > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} pageSize={pageSize} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={TAB_CARS} className="mt-0">
          <Card>
            <CardHeader className="pb-3 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.carBookingList', 'Araç kiralama satışları')} ({filteredCars.length} / DB: {carTotal})
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="ID, araç, e-posta, broker no..."
                    value={travelSearch}
                    onChange={(e) => setTravelSearch(e.target.value)}
                    className="pl-8 h-8 w-64 text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {travelLoading && carBookings.length === 0 ? (
                <div className="flex justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-xs">Kayıt</TableHead>
                      <TableHead className="font-semibold text-xs">Araç</TableHead>
                      <TableHead className="font-semibold text-xs">Müşteri</TableHead>
                      <TableHead className="font-semibold text-xs">Alış / İade</TableHead>
                      <TableHead className="font-semibold text-xs">Durum</TableHead>
                      <TableHead className="font-semibold text-xs">Ödeme</TableHead>
                      <TableHead className="font-semibold text-xs">Broker</TableHead>
                      <TableHead className="font-semibold text-xs">Tarih</TableHead>
                      <TableHead className="font-semibold text-xs text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          {carTotal === 0
                            ? t('admin.noCarBookings', 'Kayıtlı araç kiralama satışı yok.')
                            : t('admin.noCarMatch', 'Arama ile eşleşen araç satışı yok.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((b) => {
                        const c = b.customer || {};
                        const v = b.vehicle || {};
                        const s = b.search || {};
                        const pay = b.payment || {};
                        const br = b.broker || {};
                        const pricing = b.pricing || {};
                        const cur = pricing.currency || v.currencyCode || 'EUR';
                        const total = pricing.total ?? v.totalPrice;
                        const totalTry = pricing.total_try ?? pay.charge_amount;
                        return (
                          <TableRow key={b.id} className="hover:bg-muted/40">
                            <TableCell className="font-mono text-xs max-w-[110px] truncate" title={b.id}>
                              {b.id}
                            </TableCell>
                            <TableCell className="max-w-[160px]">
                              <p className="font-medium text-sm truncate">{v.vehicleName || '-'}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {[v.sippCode, v.vendorName].filter(Boolean).join(' · ') || '-'}
                              </p>
                            </TableCell>
                            <TableCell className="text-sm">
                              <span className="block truncate max-w-[150px]">
                                {c.name} {c.surname}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-[150px] block">
                                {c.email}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs max-w-[160px]">
                              <span className="block truncate" title={s.pickupLocationName}>
                                {s.pickupLocationName || '-'}
                              </span>
                              <span className="text-muted-foreground whitespace-nowrap">
                                {s.pickupDate || '-'} → {s.returnDate || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {(b.booking_status || '').replace(/_/g, ' ') || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentBadge[pay.status] || 'bg-gray-100 text-gray-800'}`}
                              >
                                {(pay.status || '—').toUpperCase()}
                              </span>
                              {typeof total === 'number' && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {formatPrice(total, cur)}
                                  {totalTry != null && cur !== 'TRY' && (
                                    <> · ≈ TRY {Number(totalTry).toFixed(0)}</>
                                  )}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              <span className="font-mono truncate max-w-[100px] block" title={br.reservation_number || ''}>
                                {br.reservation_number || '—'}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {(br.status || '').replace(/_/g, ' ') || ''}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(b.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => setTravelDetail({ type: 'car', data: b })}
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" /> JSON
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
              {orderTab === TAB_CARS && totalPages > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} pageSize={pageSize} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={TAB_LIMO} className="mt-0">
          <Card>
            <CardHeader className="pb-3 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.limoBookingList', 'Taksi & Limuzin talepleri')} ({filteredLimo.length} / DB: {limoTotal})
                </CardTitle>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="Talep no, ad, e-posta, adres..."
                    value={travelSearch}
                    onChange={(e) => setTravelSearch(e.target.value)}
                    className="pl-8 h-8 w-64 text-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {travelLoading && limoBookings.length === 0 ? (
                <div className="flex justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-xs">Talep no</TableHead>
                      <TableHead className="font-semibold text-xs">Tür</TableHead>
                      <TableHead className="font-semibold text-xs">Müşteri</TableHead>
                      <TableHead className="font-semibold text-xs">Alış / Bırakış</TableHead>
                      <TableHead className="font-semibold text-xs">Tarih</TableHead>
                      <TableHead className="font-semibold text-xs">Sınıf</TableHead>
                      <TableHead className="font-semibold text-xs">Durum</TableHead>
                      <TableHead className="font-semibold text-xs">Kayıt</TableHead>
                      <TableHead className="font-semibold text-xs text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          {limoTotal === 0
                            ? t('admin.noLimoBookings', 'Kayıtlı taksi / limuzin talebi yok.')
                            : t('admin.noLimoMatch', 'Arama ile eşleşen talep yok.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((b) => (
                        <TableRow key={b.id || b.request_number} className="hover:bg-muted/40">
                          <TableCell className="font-mono text-xs font-medium">{b.request_number || '-'}</TableCell>
                          <TableCell className="text-xs">{limoTypeLabel(b.booking_type)}</TableCell>
                          <TableCell className="text-sm">
                            <span className="block truncate max-w-[150px]">{b.contact_name || '-'}</span>
                            <span className="text-xs text-muted-foreground truncate max-w-[150px] block">{b.contact_email}</span>
                            <span className="text-[10px] text-muted-foreground block">{b.contact_phone}</span>
                          </TableCell>
                          <TableCell className="text-xs max-w-[180px]">
                            <span className="block truncate" title={b.pickup_address}>{b.pickup_address || '-'}</span>
                            <span className="text-muted-foreground block truncate" title={b.dropoff_address}>{b.dropoff_address || '-'}</span>
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{formatDate(b.pickup_datetime)}</TableCell>
                          <TableCell className="text-xs">{b.preferred_vehicle_class || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] font-normal ${statusBadge[b.status] || 'bg-gray-100 text-gray-800'}`}>
                              {limoStatusLabel(b.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(b.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => setTravelDetail({ type: 'limo', data: b })}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" /> Detay
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
              {orderTab === TAB_LIMO && totalPages > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} pageSize={pageSize} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <OrderDetailModal
        orderId={selectedOrderId}
        open={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onUpdate={() => handleOrderStatusChange?.()}
      />

      <Dialog open={!!travelDetail} onOpenChange={(o) => !o && setTravelDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{travelDetailTitle}</DialogTitle>
          </DialogHeader>
          {travelDetail?.type === 'limo' && travelDetail.data && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
              <div><dt className="text-xs text-muted-foreground">Talep no</dt><dd className="font-medium">{travelDetail.data.request_number || '-'}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Durum</dt><dd>{limoStatusLabel(travelDetail.data.status)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Tür</dt><dd>{limoTypeLabel(travelDetail.data.booking_type)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Sınıf</dt><dd>{travelDetail.data.preferred_vehicle_class || '-'}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Müşteri</dt><dd>{travelDetail.data.contact_name || '-'}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Telefon</dt><dd>{travelDetail.data.contact_phone || '-'}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">E-posta</dt><dd>{travelDetail.data.contact_email || '-'}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Alış</dt><dd>{travelDetail.data.pickup_address || '-'}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Bırakış</dt><dd>{travelDetail.data.dropoff_address || '-'}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Alış zamanı</dt><dd>{formatDate(travelDetail.data.pickup_datetime)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Yolcu</dt><dd>{travelDetail.data.passengers ?? '-'}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Not</dt><dd>{travelDetail.data.comments || '-'}</dd></div>
            </dl>
          )}
          <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-all bg-muted/50 rounded-md p-3 border">{travelDetailJson}</pre>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrdersTab;
